/**
 * Image geometry: greyscale levels, quadrilaterals, homographies and resampling.
 *
 * A phone hands over a *photograph*, not a card. The card sits in the frame with
 * margins around it, rotated by however the hand was holding it, keystoned by the
 * lens, and lit unevenly. Every measurement the cascade makes - where the art
 * window is, whether there is a band, where the footer text sits - is stated in
 * card-relative coordinates, so it needs the card mapped onto those coordinates
 * first. This module is that mapping, and nothing else: no thresholds, no
 * classification, no opinions about cards.
 *
 * Three facts about the arithmetic are worth stating, because the rest of the
 * pipeline depends on them:
 *
 *  - **A quadrilateral maps to a rectangle with a 3x3 homography**, solved from the
 *    four corner correspondences. That single object covers rotation, scale,
 *    translation *and* perspective: an affine fit would leave the keystone of a
 *    card held at an angle uncorrected, and the art window at the far end of the
 *    card would be off by the very margin this pass is trying to remove.
 *  - **Resampling is a bilinear inverse map**, which means the output is defined by
 *    asking where each output pixel came from rather than by scattering input
 *    pixels. That is what keeps a warp free of holes at any scale.
 *  - **Levels are whatever the camera's tone curve produced.** Exposure
 *    normalisation lives here too, because every threshold downstream - `paper`
 *    near 225, a band under 82 - is a statement about a printed card, and only a
 *    capture that has been put back on the printed card's scale can be compared
 *    with one.
 *
 * Coordinates are pixels with y down and the origin at the image's top-left, and
 * card-relative coordinates are the same thing divided by width and height, so a
 * measurement never depends on how many pixels the capture happened to have.
 */
export const isGreyImage = (image) => Number.isInteger(image.width) &&
    Number.isInteger(image.height) &&
    image.width > 0 &&
    image.height > 0 &&
    image.levels.length === image.width * image.height;
export function assertGreyImage(image, what) {
    if (!Number.isInteger(image.width) || !Number.isInteger(image.height) || image.width < 1 || image.height < 1) {
        throw new Error(`${what} has a non-positive size ${image.width}x${image.height}`);
    }
    if (image.levels.length !== image.width * image.height) {
        throw new Error(`${what} says ${image.width}x${image.height} but holds ${image.levels.length} levels, not ${image.width * image.height}`);
    }
}
export const rectQuad = (rect) => [
    { x: rect.x0, y: rect.y0 },
    { x: rect.x1, y: rect.y0 },
    { x: rect.x1, y: rect.y1 },
    { x: rect.x0, y: rect.y1 },
];
/** The rectangle a quad spans, axis-aligned. */
export function quadBounds(quad) {
    const xs = quad.map((point) => point.x);
    const ys = quad.map((point) => point.y);
    return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}
/**
 * Solve the 3x3 homography mapping `from` to `to`, row-major with `m[8] = 1`.
 *
 * Eight unknowns, eight equations, one 8x8 Gauss-Jordan elimination with partial
 * pivoting. Written out rather than pulled from a linear-algebra dependency: it is
 * twenty lines, the pipeline needs exactly this one solve, and a 3x3 matrix has no
 * numerical delicacy a library would handle better here. A degenerate quad (three
 * collinear corners) is refused rather than returned as a singular map.
 */
export function solveHomography(from, to) {
    const matrix = [];
    for (let index = 0; index < 4; index += 1) {
        const source = from[index];
        const target = to[index];
        matrix.push([
            source.x,
            source.y,
            1,
            0,
            0,
            0,
            -target.x * source.x,
            -target.x * source.y,
            target.x,
        ]);
        matrix.push([
            0,
            0,
            0,
            source.x,
            source.y,
            1,
            -target.y * source.x,
            -target.y * source.y,
            target.y,
        ]);
    }
    for (let column = 0; column < 8; column += 1) {
        let pivot = column;
        for (let row = column + 1; row < 8; row += 1) {
            if (Math.abs(matrix[row][column]) > Math.abs(matrix[pivot][column])) {
                pivot = row;
            }
        }
        const pivotValue = matrix[pivot][column];
        if (Math.abs(pivotValue) < 1e-12) {
            throw new Error('the four corners of this quad are degenerate: they do not define a mapping');
        }
        if (pivot !== column) {
            const swap = matrix[column];
            matrix[column] = matrix[pivot];
            matrix[pivot] = swap;
        }
        const row = matrix[column];
        for (let index = column; index <= 8; index += 1) {
            row[index] = row[index] / pivotValue;
        }
        for (let other = 0; other < 8; other += 1) {
            if (other === column)
                continue;
            const target = matrix[other];
            const factor = target[column];
            if (factor === 0)
                continue;
            for (let index = column; index <= 8; index += 1) {
                target[index] = target[index] - factor * row[index];
            }
        }
    }
    const solution = matrix.map((row) => row[8]);
    return [...solution, 1];
}
export function applyHomography(matrix, point) {
    const denominator = matrix[6] * point.x + matrix[7] * point.y + matrix[8];
    if (Math.abs(denominator) < 1e-12)
        throw new Error('this homography maps a point to infinity');
    return {
        x: (matrix[0] * point.x + matrix[1] * point.y + matrix[2]) / denominator,
        y: (matrix[3] * point.x + matrix[4] * point.y + matrix[5]) / denominator,
    };
}
/** The inverse map, as a homography of its own. */
export function invertHomography(matrix) {
    const [a, b, c, d, e, f, g, h, i] = matrix;
    const A = e * i - f * h;
    const B = -(d * i - f * g);
    const C = d * h - e * g;
    const determinant = a * A + b * B + c * C;
    if (Math.abs(determinant) < 1e-12)
        throw new Error('this homography is singular and cannot be inverted');
    const inverse = [
        A,
        -(b * i - c * h),
        b * f - c * e,
        B,
        a * i - c * g,
        -(a * f - c * d),
        C,
        -(a * h - b * g),
        a * e - b * d,
    ].map((value) => value / determinant);
    const scale = inverse[8];
    if (Math.abs(scale) < 1e-12)
        throw new Error('this homography is singular and cannot be inverted');
    return inverse.map((value) => value / scale);
}
/** Bilinear sample, clamped at the edges: a warp never reads outside the image. */
export function bilinear(image, x, y) {
    const clampedX = Math.min(image.width - 1, Math.max(0, x));
    const clampedY = Math.min(image.height - 1, Math.max(0, y));
    const x0 = Math.floor(clampedX);
    const y0 = Math.floor(clampedY);
    const x1 = Math.min(image.width - 1, x0 + 1);
    const y1 = Math.min(image.height - 1, y0 + 1);
    const fx = clampedX - x0;
    const fy = clampedY - y0;
    const topLeft = image.levels[y0 * image.width + x0] ?? 0;
    const topRight = image.levels[y0 * image.width + x1] ?? 0;
    const bottomLeft = image.levels[y1 * image.width + x0] ?? 0;
    const bottomRight = image.levels[y1 * image.width + x1] ?? 0;
    const top = topLeft + (topRight - topLeft) * fx;
    const bottom = bottomLeft + (bottomRight - bottomLeft) * fx;
    return top + (bottom - top) * fy;
}
/**
 * Resample the quad `from` of `image` into a `width x height` image.
 *
 * The output's rectangle maps onto `from`, so the returned image is the quad
 * straightened out with its top-left corner at the output's top-left.
 */
export function warpQuad(image, from, width, height, options = {}) {
    assertGreyImage(image, 'the image to warp');
    if (width < 1 || height < 1)
        throw new Error(`cannot warp into a ${width}x${height} image`);
    const unit = [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
        { x: 1, y: 1 },
        { x: 0, y: 1 },
    ];
    // A resampler asks, for each output pixel, where it came from: the unit square
    // mapped onto the source quad, which is the inverse of the client's own direction.
    const inverse = solveHomography(unit, from);
    void options;
    const levels = new Uint8Array(width * height);
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            const source = applyHomography(inverse, { x: (x + 0.5) / width, y: (y + 0.5) / height });
            levels[y * width + x] = Math.round(Math.min(255, Math.max(0, bilinear(image, source.x, source.y))));
        }
    }
    return { levels, width, height };
}
/** The largest of the four corner distances between two quads, in pixels. */
export function quadDistance(a, b) {
    let worst = 0;
    for (let index = 0; index < 4; index += 1) {
        const left = a[index];
        const right = b[index];
        worst = Math.max(worst, Math.hypot(left.x - right.x, left.y - right.y));
    }
    return worst;
}
/** A histogram of 256 bins, so percentiles are a lookup rather than a sort. */
function histogram(image) {
    const counts = new Int32Array(256);
    for (let index = 0; index < image.levels.length; index += 1) {
        const level = image.levels[index];
        counts[level] = (counts[level] ?? 0) + 1;
    }
    return counts;
}
function percentile(counts, total, fraction) {
    const target = Math.max(0, Math.min(total - 1, Math.round(fraction * total)));
    let seen = 0;
    for (let level = 0; level < 256; level += 1) {
        seen += counts[level];
        if (seen > target)
            return level;
    }
    return 255;
}
/**
 * Put a capture back on the printed card's scale.
 *
 * Every threshold downstream is a statement about a printed card - a band is under
 * 82, paper is over 225 - and a phone's tone curve moves those numbers wholesale: a
 * dim room, a warm lamp and an auto-exposure that metered the hand instead of the
 * card can put printed white at 190 or 250. Normalising the *percentiles* rather
 * than the range is deliberate: a card has a lot of paper and very little pure black,
 * so its 5th percentile is a shadow rather than the frame's black border, and the
 * two ends stay put across light levels.
 */
export function normalizeExposure(image, options = {}) {
    const white = options.white ?? 236;
    const black = options.black ?? 22;
    const whiteFraction = options.whiteFraction ?? 0.9;
    const blackFraction = options.blackFraction ?? 0.05;
    const counts = histogram(image);
    const high = percentile(counts, image.levels.length, whiteFraction);
    const low = percentile(counts, image.levels.length, blackFraction);
    const span = Math.max(8, high - low);
    const gain = (white - black) / span;
    const levels = new Uint8Array(image.levels.length);
    for (let index = 0; index < image.levels.length; index += 1) {
        levels[index] = Math.round(Math.min(255, Math.max(0, black + (image.levels[index] - low) * gain)));
    }
    return { levels, width: image.width, height: image.height };
}
/** Downscale by an integer factor of 1, 2 or 3 with box averages, or return a copy. */
export function downscale(image, factor) {
    if (!Number.isInteger(factor) || factor < 1 || factor > 3) {
        throw new Error(`a downscale factor of ${factor} is not supported; use 1, 2 or 3`);
    }
    if (factor === 1)
        return { levels: image.levels.slice(), width: image.width, height: image.height };
    const width = Math.max(1, Math.floor(image.width / factor));
    const height = Math.max(1, Math.floor(image.height / factor));
    const levels = new Uint8Array(width * height);
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            let sum = 0;
            let count = 0;
            for (let dy = 0; dy < factor; dy += 1) {
                const sy = y * factor + dy;
                if (sy >= image.height)
                    break;
                for (let dx = 0; dx < factor; dx += 1) {
                    const sx = x * factor + dx;
                    if (sx >= image.width)
                        break;
                    sum += image.levels[sy * image.width + sx] ?? 0;
                    count += 1;
                }
            }
            levels[y * width + x] = Math.round(sum / Math.max(1, count));
        }
    }
    return { levels, width, height };
}
//# sourceMappingURL=geometry.js.map